import axios from "axios";
import { getToken, onMessage } from "firebase/messaging";
import { getFirebaseMessaging, isFirebaseConfigured } from "./firebase";

const apiUrl = process.env.REACT_APP_API_URL;
const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

async function ensureMessagingServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;

  const existing = await navigator.serviceWorker.getRegistration(
    "/firebase-messaging-sw.js",
  );
  if (existing) return existing;

  return navigator.serviceWorker.register("/firebase-messaging-sw.js");
}

export async function saveFcmTokenToServer(email, fcmToken) {
  if (!email || !fcmToken) return false;

  const response = await axios.post(`${apiUrl}/public/save-token`, {
    email,
    fcmToken,
  });

  return Boolean(response?.data?.success);
}

/**
 * Ask permission, get FCM token, save against customer email.
 * Safe to call multiple times (idempotent).
 */
export async function registerAndSaveFcmToken(email) {
  try {
    if (!email) {
      console.warn("[FCM] skipped: no email");
      return { success: false, reason: "no_email" };
    }

    if (!isFirebaseConfigured()) {
      console.warn(
        "[FCM] skipped: missing REACT_APP_FIREBASE_* env values",
      );
      return { success: false, reason: "not_configured" };
    }

    if (!("Notification" in window)) {
      console.warn("[FCM] skipped: Notification API unsupported");
      return { success: false, reason: "unsupported" };
    }

    const permission =
      Notification.permission === "granted"
        ? "granted"
        : await Notification.requestPermission();

    if (permission !== "granted") {
      console.warn("[FCM] permission denied");
      return { success: false, reason: "permission_denied" };
    }

    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      console.warn("[FCM] messaging unavailable in this browser");
      return { success: false, reason: "messaging_unavailable" };
    }

    const registration = await ensureMessagingServiceWorker();
    const fcmToken = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration || undefined,
    });

    if (!fcmToken) {
      console.warn("[FCM] empty token from Firebase");
      return { success: false, reason: "empty_token" };
    }

    await saveFcmTokenToServer(email, fcmToken);
    localStorage.setItem("fcmToken", fcmToken);
    console.log("[FCM] token saved for", email);
    return { success: true, fcmToken };
  } catch (error) {
    console.error("[FCM] register failed:", error);
    return { success: false, reason: "error", error: error?.message };
  }
}

/** Show foreground toast-style browser notification when app is open */
export async function listenForForegroundMessages() {
  try {
    if (!isFirebaseConfigured()) return;
    const messaging = await getFirebaseMessaging();
    if (!messaging) return;

    onMessage(messaging, (payload) => {
      const title = payload?.notification?.title || "Danny's Game Room";
      const body = payload?.notification?.body || "";
      if (Notification.permission === "granted") {
        new Notification(title, { body, icon: "/logo192.png" });
      }
    });
  } catch (error) {
    console.error("[FCM] foreground listener failed:", error);
  }
}
